import { useMemo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";

// Top/bottom IP per numero di log. `data` arriva ordinato per count crescente:
// i primi sono i meno frequenti, gli ultimi i più frequenti.

const BAR_COLOR = "#449095";
const MUTED = "rgba(234,234,234,0.5)";

// Numero di log in testo + barra proporzionale al massimo della propria lista.
const IpRow = ({ ip, count, max }) => (
  <Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontFamily: "monospace" }} noWrap>{ip}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{Number(count).toLocaleString()}</Typography>
    </Box>
    <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, backgroundColor: "#333", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: `${max > 0 ? Math.max(2, (count / max) * 100) : 0}%`, backgroundColor: BAR_COLOR }} />
    </Box>
  </Box>
);

const Section = ({ title, caption, items, loading }) => {
  const max = items.reduce((m, i) => Math.max(m, i.count), 0);
  return (
    <Box sx={{ flex: "0 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: MUTED, mb: 1.25 }}>{caption}</Typography>
      {/* Altezza data dal contenuto: con poche righe non si allungano; scroll solo se manca spazio. */}
      <Box sx={{ minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
        {loading ? (
          [...Array(10)].map((_, i) => (
            <Box key={i}>
              <Skeleton variant="text" width="60%" sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
              <Skeleton variant="rounded" height={4} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
            </Box>
          ))
        ) : items.length > 0 ? (
          items.map((item) => <IpRow key={item._id} ip={item._id} count={item.count} max={max} />)
        ) : (
          <Typography sx={{ fontSize: 13, color: MUTED }}>No data</Typography>
        )}
      </Box>
    </Box>
  );
};

const ClassificationCard = ({ data, loading }) => {
  const { top, bottom } = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    // Copie: non modificare l'array ricevuto dal genitore.
    if (list.length >= 20) {
      return { top: list.slice(-10).reverse(), bottom: list.slice(0, 10) };
    }
    return { top: [...list].reverse(), bottom: [] };
  }, [data]);

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Section title="Top 10" caption="IPs with the most logs" items={top} loading={loading} />
      {(loading || bottom.length > 0) && (
        <>
          <Box sx={{ borderTop: "1px solid #292929" }} />
          <Section title="Bottom 10" caption="IPs with the fewest logs" items={bottom} loading={loading} />
        </>
      )}
    </Box>
  );
};

export default ClassificationCard;
